<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import {
	getCurrentInstance,
	computed,
	ref,
	watch,
	onMounted,
	onUnmounted,
} from "vue"
import { useStore } from "vuex"
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import { mediaPulseModel } from "@/components/Composable/mediaPulse"
import { buildStreamPreviewSpecs } from "@/Utils/previewHost"
import TranscoderService from "@/services/TranscoderService"
import Spinner from "@/components/ui/Spinner.vue"
import ConfirmModal from "@/components/modals/ConfirmModal.vue"
import ToggleStreamModal from "@/components/modals/ToggleStreamModal.vue"
import StreamSidebar from "../../components/ui/StreamSidebar.vue"
import AlertModal from "@/components/modals/AlertModal.vue"
import TranscoderSettings from "../../components/Transcoder/TranscoderSettings.vue"
import TranscoderSources from "../../components/Transcoder/TranscoderSources.vue"
import Title from "../../components/ui/Title.vue"
import BackButton from '@/components/Atoms/BackButton.vue'
import HeaderHelperLinks from "@/components/Moleculas/HeaderHelperLinks.vue"
import Alert from "../../components/Atoms/Alert.vue"

const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

// Refs
const destination = ref(null)
const transcoderWrapp = ref(null)

const loading = ref(false)
const scopeAlive = ref(true)
const streamPreviewSpecsUrl = ref(null)
const streamPreviewSpecsType = ref(null)

const streamAlive = ref(false)

const toggleProcessing = ref(false)
const transcoderTitleTmp = ref('')

// Media Pulse
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

// Computed
const transcoder = computed(() => store.getters["Transcoders/transcoder"])
// The store keeps the last opened transcoder, so only render once it matches the route.
const transcoderReady = computed(() => !!transcoder.value?._id && transcoder.value._id === route.params?.transcoderId)
const allInOneStreams = computed(
	() => store.getters["Streams/allInOneStreams"]
)
const userLoaded = computed(() => store.getters['User/userLoaded'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const planEnded = computed(() =>
	userLoaded.value && (!hasLiveSubscription.value || !hasLiveSubscription.value.enabled)
)

const destinationStream = computed(() => {
	if (transcoder.value && destination.value) {
		return allInOneStreams.value.find(
			(stream) => stream._id === destination.value?._id
		)
	}
	return
})

const streamToggleStatus = computed(() =>
	!!destinationStream.value && (transcoder.value?.destinationEnabled ?? false)
)

watch(
	() => mediaPulse.value,
	() => {
		streamAlive.value = transcoder.value.enabled && mediaPulse.value.alive
		const specs = buildStreamPreviewSpecs(
			mediaPulse.value,
			transcoder.value?.key,
			transcoder.value
		)
		streamPreviewSpecsType.value = specs.type || null
		streamPreviewSpecsUrl.value = specs.url || null
	}
)

// Hooks
const applyTranscoderToView = ({ keepUiRegion = false } = {}) => {
	destination.value = transcoder.value.destination
	transcoderWrapp.value = {
		hostname: "n/a",
		...transcoder.value,
		uiRegion: (keepUiRegion && transcoderWrapp.value?.uiRegion) || transcoder.value?.region || {},
		type: "live",
	}
	transcoderTitleTmp.value = transcoder.value.name
}

onMounted(async () => {
	const transcoderId = route.params?.transcoderId
	store.dispatch("Streams/getStreams")

	const seeded = (await store.dispatch("Transcoders/seedTranscoder", transcoderId)) || transcoderReady.value
	if (seeded) {
		applyTranscoderToView()
		subscribeMediaPulse(transcoder.value)
	}

	try {
		await store.dispatch("Transcoders/getTranscoder", transcoderId)
	} catch (err) {
		console.error(err)
		if (!scopeAlive.value) return
		notify({
			group: "error",
			text: "Failed to load transcoder data",
		})
		await router.push({ name: "ChannelList" });
		return
	}
	if (!scopeAlive.value) return

	applyTranscoderToView({ keepUiRegion: seeded })
	if (!seeded) subscribeMediaPulse(transcoder.value)
	store.dispatch("Transcoders/getTranscoders")
})

onUnmounted(async () => {
	scopeAlive.value = false
	unsubscribeMediaPulse()
})

// Methods
const onTranscoderUpdate = (newRegion) => {
	transcoderWrapp.value.uiRegion = newRegion.uiRegion
}

const onTranscoderToggle = () => {
	showModal("modalToggleStream" + transcoder.value._id)
}

const onToggleDone = async () => {
	await store.dispatch('Transcoders/getTranscoders')
	const response = await store.dispatch("Transcoders/getTranscoder", transcoder.value._id)
	console.log(response);
	destination.value = transcoder.value.destination
	transcoderWrapp.value = {
		hostname: "n/a",
		uiRegion: transcoder.value?.region ?? {},
		...transcoder.value,
		type: "live",
	}
}

const onTranscoderDelete = () => {
	showModal("delete-transcoder")
}

const onTranscoderDeleteConfirm = async () => {
	try {
		await TranscoderService.deleteTranscoder(transcoder.value._id)
		// Force refresh to remove deleted transcoder from store
		await store.dispatch("Transcoders/getTranscoders", true)
		notify({
			group: "success",
			text: "Transcoder profile deleted",
		})

		setTimeout(() => {
			router.push({
				name: "TranscoderDashboard",
			})
		}, 300)

		return
	} catch (err) {
		console.error(err)
		notify({
			group: "error",
			text: "Unable to delete the transcoder.",
		})
	}
}

// Used by the <Title> component (emits the new name string).
const onTranscoderNameChange = async (newName) => {
	if (!newName || newName.length <= 1 || newName === transcoderTitleTmp.value) return
	try {
		await TranscoderService.updateTranscoder(transcoder.value._id, { name: newName })
		transcoderTitleTmp.value = newName
		notify({ group: "info", text: "Transcoder was renamed" })
	} catch (err) {
		notify({ group: "error", text: "Transcoder rename failed" })
	}
}

const showDeleteModal = () => {
	showModal("delete-transcoder")
}

</script>

<template>
	<div class="w-full container detail-shell px-4 mt-4 lg:pt-8 lg:self-start">
		<div class="flex items-center justify-center min-h-[70vh]" v-if="!transcoderReady" aria-busy="true">
			<Spinner classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
		</div>
		<template v-else>
			<!-- Full-width primary header -->
			<header class="flex items-center gap-x-4 mb-3 px-4 lg:px-0">
				<BackButton to="/transcoder" />
				<Title v-if="transcoder" :editable="true" :title="transcoder.name" @changed="onTranscoderNameChange"
					titleClasses="-mr-2 px-[0.5rem] rounded-md hover:bg-surface-3 line-clamp-2 text-lg font-semibold min-h-8 min-w-0"
					edit-class="bg-surface-3 line-clamp-none" />
				<HeaderHelperLinks class="ml-auto !hidden md:!flex"
					howItWorksLink="https://docs.castr.com/en/articles/8702304-how-to-transcode-your-stream"
					howItWorksText="How Transcoder works" tutorialLink="0MPTCZa5OJIYRebzIe6pY" />
			</header>

			<Alert v-if="planEnded" type="error" class="mb-4 mx-4 lg:mx-0">
				<span>Your plan has ended. Please <a class="underline hover:text-white" target="_blank"
						href="/app/subscribe">upgrade</a> to continue.</span>
			</Alert>

			<!-- Source → Transcode → Output pipeline -->
			<div
				class="grid grid-cols-1 lg:grid-cols-[400px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,2fr)_minmax(0,1fr)] 2xl:grid-cols-[400px_minmax(0,2fr)_minmax(0,1fr)] gap-4 items-start">
				<aside>
					<StreamSidebar v-if="transcoderWrapp" :stream="transcoderWrapp" :stream-alive="streamAlive"
						:media-pulse="mediaPulse" :transcoderView="true" @show-stream-toggle-modal="onTranscoderToggle"
						@toggle-transcoder="onTranscoderToggle" @delete-transcoder="onTranscoderDelete"
						@stream-updated="onTranscoderUpdate" />
				</aside>
				<section class="lg:row-span-2 xl:row-span-1">
					<TranscoderSettings :transcoder="transcoder" :stream-alive="streamAlive" />
				</section>
				<aside>
					<TranscoderSources :transcoder="transcoder" :stream-alive="streamAlive" :media-pulse="mediaPulse" />
				</aside>
			</div>
		</template>
	</div>

	<confirm-modal message="Would you like to delete this transcoder?" modal-id="delete-transcoder"
		modal-type="danger" @modal-confirm="onTranscoderDeleteConfirm" />

	<toggle-stream-modal v-if="transcoder" :stream="transcoder" @modal-toggle="onTranscoderToggleConfirm"
		:transcoderView="true" @toggle-done="onToggleDone" />
</template>
